import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import {
  Button,
  FormSection,
  Input,
  PageShell,
  SectionCard,
  Skeleton,
} from '@/components/ui';
import { useAdminAccountQuery } from '../hooks/useAdminAccountsQuery';
import { useAdminAccountMutations } from '../hooks/useAdminAccountMutations';
import { useAdminRolesQuery } from '../hooks/useAdminRolesQuery';
import { AdminRoleChips } from '../components/AdminRoleChips';
import { AdminRoleSelectDialog } from '../components/AdminRoleSelectDialog';
import { AdminPasswordResetSection } from '../components/AdminPasswordResetSection';
import { useCurrentAdmin, useHasRole } from '@/providers/AuthProvider';
import { ADMIN_PASSWORD_MIN_LENGTH, isValidAdminPassword } from '../lib/adminPasswordPolicy';
import type { AdminRole } from '../types/admin';

interface AdminFormState {
  loginId: string;
  name: string;
  email: string;
  password: string;
  roles: ReadonlyArray<AdminRole>;
}

const EMPTY_FORM: AdminFormState = {
  loginId: '',
  name: '',
  email: '',
  password: '',
  roles: [],
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const AdminAccountFormPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { adminUserId: idParam } = useParams<{ adminUserId: string }>();
  const isNew = idParam === 'new' || idParam == null;
  const adminUserId = isNew ? null : idParam;

  const detailQuery = useAdminAccountQuery(adminUserId);
  // QA-424 — 최고관리자만, 다른 관리자 계정에서만 비밀번호 초기화(본인은 [내 정보]의 비밀번호 변경).
  const isSuperAdmin = useHasRole(['SUPER_ADMIN']);
  const me = useCurrentAdmin();
  const canResetPassword = !isNew && adminUserId != null && isSuperAdmin && me?.adminUserId !== adminUserId;
  const rolesQuery = useAdminRolesQuery();
  const { create, update, replaceRoles } = useAdminAccountMutations();

  const [form, setForm] = useState<AdminFormState>(EMPTY_FORM);
  const [initialRoles, setInitialRoles] = useState<ReadonlyArray<AdminRole>>([]);
  const [error, setError] = useState<string | null>(null);
  const [rolesDialog, setRolesDialog] = useState(false);

  useEffect(() => {
    if (!detailQuery.data) return;
    const d = detailQuery.data;
    setForm({
      loginId: d.loginId,
      name: d.name,
      email: d.email,
      password: '',
      roles: d.roles,
    });
    setInitialRoles(d.roles);
  }, [detailQuery.data]);

  const set = <K extends keyof AdminFormState>(key: K, value: AdminFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const canSubmit = useMemo(() => {
    if (form.name.trim() === '' || !EMAIL_RE.test(form.email)) return false;
    if (isNew) {
      return (
        form.loginId.trim() !== '' &&
        isValidAdminPassword(form.password) &&
        form.roles.length >= 1
      );
    }
    return true;
  }, [form, isNew]);

  const rolesChanged = useMemo(() => {
    if (initialRoles.length !== form.roles.length) return true;
    const a = [...initialRoles].sort();
    const b = [...form.roles].sort();
    return a.some((r, i) => r !== b[i]);
  }, [initialRoles, form.roles]);

  const handleSubmit = async () => {
    setError(null);
    if (!canSubmit) {
      setError(t('admin.accounts.form.validation'));
      return;
    }
    try {
      if (isNew) {
        await create.mutateAsync({
          loginId: form.loginId.trim(),
          email: form.email.trim(),
          name: form.name.trim(),
          password: form.password,
          roles: [...form.roles],
        });
      } else if (adminUserId) {
        await update.mutateAsync({
          adminUserId,
          body: { email: form.email.trim(), name: form.name.trim() },
        });
        if (rolesChanged) {
          await replaceRoles.mutateAsync({ adminUserId, body: { roles: [...form.roles] } });
        }
      }
      navigate('/admin/accounts');
    } catch (e) {
      setError(friendlyMessage(e, t('admin.accounts.form.saveFailed')));
    }
  };

  const isBusy = create.isPending || update.isPending || replaceRoles.isPending;

  const breadcrumb = [
    { label: t('admin.accounts.list.title'), href: '/admin/accounts' },
    { label: isNew ? t('admin.accounts.form.createTitle') : t('admin.accounts.form.editTitle') },
  ];

  if (!isNew && detailQuery.isPending) {
    return (
      <PageShell title={t('admin.accounts.form.editTitle')} breadcrumb={breadcrumb}>
        <div className="space-y-3" role="status" aria-live="polite">
          <Skeleton className="h-[44px] w-full" />
          <Skeleton className="h-[200px] w-full" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={isNew ? t('admin.accounts.form.createTitle') : t('admin.accounts.form.editTitle')}
      breadcrumb={breadcrumb}
      actions={
        <Button size="sm" onClick={handleSubmit} disabled={isBusy || !canSubmit}>
          {t('admin.common.save')}
        </Button>
      }
    >
      {error ? (
        <div
          className="rounded-md p-3 text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          role="alert"
        >
          {error}
        </div>
      ) : null}

      <SectionCard title={t('admin.accounts.form.sectionBasic')}>
        <div className="flex flex-col gap-3">
          <FormSection label={t('admin.accounts.form.fields.name')} density="compact" required>
            <Input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              maxLength={100}
              required
            />
          </FormSection>
          <FormSection label={t('admin.accounts.form.fields.email')} density="compact" required>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              required
            />
          </FormSection>
          <FormSection label={t('admin.accounts.form.fields.loginId')} density="compact" required={isNew}>
            <Input
              value={form.loginId}
              onChange={(e) => set('loginId', e.target.value)}
              maxLength={64}
              disabled={!isNew}
              required={isNew}
            />
          </FormSection>
          {isNew ? (
            <FormSection
              label={t('admin.accounts.form.fields.password')}
              density="compact"
              required
              hint={t('admin.accounts.form.fields.passwordHint')}
            >
              <Input
                type="password"
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                minLength={ADMIN_PASSWORD_MIN_LENGTH}
                maxLength={100}
                autoComplete="new-password"
                required
              />
            </FormSection>
          ) : null}
        </div>
      </SectionCard>

      <SectionCard
        title={t('admin.accounts.form.sectionRoles')}
        aside={
          <Button variant="outline" size="sm" onClick={() => setRolesDialog(true)}>
            {t('admin.accounts.form.editRoles')}
          </Button>
        }
      >
        <AdminRoleChips roles={form.roles} />
        {form.roles.length === 0 ? (
          <p className="mt-2 text-xs" style={{ color: 'var(--content-muted)' }}>
            {t('admin.accounts.form.rolesRequired')}
          </p>
        ) : null}
      </SectionCard>

      {canResetPassword && adminUserId ? (
        <AdminPasswordResetSection adminUserId={adminUserId} adminName={detailQuery.data?.name ?? form.name} />
      ) : null}

      <SectionCard title={t('admin.accounts.form.sectionTwoFactor')}>
        {/* 2FA enroll / QR / backup codes are not implemented in user-service yet.
            Disabled until the BE endpoints (enroll/verify/backup-codes) ship. */}
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm" style={{ color: 'var(--content-muted)' }}>
            {t('admin.accounts.form.twoFactorPending')}
          </p>
          <Button variant="outline" size="sm" disabled>
            {t('admin.accounts.form.twoFactorEnroll')}
          </Button>
        </div>
      </SectionCard>

      <AdminRoleSelectDialog
        open={rolesDialog}
        onOpenChange={setRolesDialog}
        roleViews={rolesQuery.data ?? []}
        value={form.roles}
        onApply={(roles) => {
          set('roles', [...roles]);
          setRolesDialog(false);
        }}
      />
    </PageShell>
  );
};
